import { useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2Icon,
  LandmarkIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  SettingsIcon,
  ShieldIcon,
  SunMoonIcon,
  SunIcon,
  UserIcon,
  WrenchIcon
} from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useSession } from '@/components/auth/user-provider';
import { CurrentUserGroupRecovery } from '@/components/auth/group-access-recovery';
import { useManagedMode } from '@/components/layout/managed-mode-provider';
import { useTheme, type Theme } from '@/components/theme/theme-provider';
import { defaultEntity, entitySwitchPath, selectedEntity, tenantChoices } from '@/features/tenancy/context-navigation';
import { PRODUCT_ROLE_LABELS, type ProductRoleCode } from '@/lib/product-roles';

function productRoleLabel(roles: string[]): string {
  if (roles.length === 0) return 'None';
  return roles
    .map((r) =>
      r in PRODUCT_ROLE_LABELS
        ? PRODUCT_ROLE_LABELS[r as ProductRoleCode]
        : r.replace(/_/g, ' ')
    )
    .join(', ');
}

export function ProfileMenu() {
  const { session, signOut, viewAsOptions, setViewAs, clearViewAs } = useSession();
  const { theme, setTheme } = useTheme();
  const managedMode = useManagedMode();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  if (session.status !== 'signedIn') return null;
  const { user } = session;
  const canSwitch = viewAsOptions.length > 0;
  const radioValue = user.viewAsRole ?? '__real__';
  const activeEntity = selectedEntity(user.tenants, location.pathname);
  const groups = tenantChoices(user.tenants);
  const activeTenant = groups.find((group) => group.id === activeEntity?.tenantId) ?? groups[0];
  const switchEntity = (entity: (typeof user.tenants)[number]) => {
    if (activeEntity?.id === entity.id) return;
    queryClient.clear();
    navigate(entitySwitchPath(entity));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" aria-label="Open profile menu">
          <Avatar>
            <AvatarFallback className="text-xs font-medium">{user.initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium">{user.name}</p>
            <p className="text-muted-foreground truncate text-xs">{user.email}</p>
            <p className="text-muted-foreground text-xs">
              Access: {productRoleLabel(user.realProductRoles)}
              {user.viewAsRole ? (
                <span className="text-amber-600 dark:text-amber-400">
                  {' '}
                  · previewing {PRODUCT_ROLE_LABELS[user.viewAsRole]}
                </span>
              ) : null}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/settings">
              <UserIcon /> Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings">
              <SettingsIcon /> Settings
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        {activeTenant ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <LandmarkIcon /> Switch tenant
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-64">
                <DropdownMenuRadioGroup
                  value={activeEntity?.tenantId ?? ''}
                  onValueChange={(tenantId) => {
                    const choice = groups.find((group) => group.id === tenantId);
                    const entity = choice && defaultEntity(choice.entities);
                    if (entity) switchEntity(entity);
                  }}
                >
                  {groups.map((group) => (
                    <DropdownMenuRadioItem key={group.id} value={group.id}>
                      <span className="truncate">{group.name}</span>
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Building2Icon /> Switch entity
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-64">
                <DropdownMenuLabel className="text-muted-foreground truncate text-xs font-normal">
                  {activeTenant.name}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={activeEntity?.slug ?? ''}
                  onValueChange={(slug) => {
                    const entity = activeTenant.entities.find((item) => item.slug === slug);
                    if (entity) switchEntity(entity);
                  }}
                >
                  {activeTenant.entities.map((entity) => (
                    <DropdownMenuRadioItem key={entity.id} value={entity.slug}>
                      <span className="truncate">{entity.name}</span>
                      {entity.status !== 'active' ? <span className="text-muted-foreground ml-auto text-xs capitalize">{entity.status}</span> : null}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        ) : null}
        <CurrentUserGroupRecovery />
        {managedMode.available ? <>
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem checked={managedMode.enabled}
            onCheckedChange={managedMode.setEnabled}>
            <WrenchIcon className="size-4" /> Managed mode
          </DropdownMenuCheckboxItem>
        </> : null}
        {canSwitch ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <ShieldIcon /> View as role
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-56">
                <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                  Use the app with that role's own permissions, including its booking and
                  administration tools. Organisation settings stay off. Changes made while
                  previewing are real and in your name. Your assigned access is unchanged.
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup
                  value={radioValue}
                  onValueChange={(value) => {
                    if (value === '__real__') {
                      clearViewAs();
                      return;
                    }
                    setViewAs(value as ProductRoleCode);
                  }}
                >
                  <DropdownMenuRadioItem value="__real__">
                    Your real access
                  </DropdownMenuRadioItem>
                  {viewAsOptions.map((role) => (
                    <DropdownMenuRadioItem key={role} value={role}>
                      {PRODUCT_ROLE_LABELS[role]}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <SunMoonIcon /> Theme
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={theme}
              onValueChange={(value) => setTheme(value as Theme)}
            >
              <DropdownMenuRadioItem value="light">
                <SunIcon className="size-4" /> Light
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">
                <MoonIcon className="size-4" /> Dark
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system">
                <MonitorIcon className="size-4" /> System
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={signOut}>
          <LogOutIcon /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
